<template>
  <div
    class="cm-card"
    :class="{ 'cm-card--critica': item.otCritica, 'cm-card--click': !detalle }"
    @click="!detalle && $emit('detalle')"
  >
    <!-- Franja lateral: roja si el cambio ya se pasó de hora, azul si va a tiempo. -->
    <div class="cm-card-accent" :style="{ background: colorLateral }"></div>

    <!-- Molde que entra y máquina, a la misma altura. Misma distribución que las tarjetas del
         estado de máquinas: etiqueta chica arriba y el código debajo. -->
    <div class="cm-card-header">
      <div class="cm-block">
        <span class="cm-block-label">Siguiente molde</span>
        <span class="cm-molde-code">{{ item.moldeSiguiente || '—' }}</span>
      </div>
      <div class="cm-block cm-block--der">
        <span class="cm-block-label">Máquina</span>
        <span class="cm-maq-code">{{ item.codigoMaquina || '—' }}</span>
      </div>
    </div>

    <!-- Hora del cambio. El tiempo de retraso solo se muestra en el detalle. -->
    <div class="cm-tiempo">
      <span class="cm-hora">{{ horaTxt }}</span>
      <span v-if="detalle && faltanteTxt" class="cm-falta" :class="{ 'cm-falta--retraso': retrasado }">
        {{ faltanteTxt }}
      </span>
      <!-- La máquina trae una OT en curso: el molde está montado y el cambio no puede arrancar
           hasta que libere. Es el cuarto criterio de orden de las tarjetas. -->
      <span v-if="detalle && item.maqOcupada" class="cm-ocupada">
        <span class="cm-ocupada-dot"></span>
        Maq. Ocupada
      </span>
    </div>

    <!-- Clave de la OT que entra. Solo en el detalle: en la lista no cabe. -->
    <div v-if="detalle && item.claveSiguiente" class="cm-ot">
      OT: <b class="cm-ot-dato">{{ item.claveSiguiente }}</b>
    </div>

    <!-- Artículos / cavidades de la OT que entra. La meta por artículo solo en el detalle. -->
    <div v-if="cavidades.length" class="cm-arts">
      <div
        v-for="(ca, i) in cavidades"
        :key="'cm-art-' + i"
        class="cm-art-row"
        :class="{ 'cm-art-row--detalle': detalle }"
      >
        <span class="cm-art-dot"></span>
        <span class="cm-art-name">{{ ca.nombre }}{{ sufijoLado(ca.nombre) }}</span>
        <span v-if="detalle && ca.meta" class="cm-art-meta">{{ numTxt(ca.meta) }} pzas</span>
        <span class="cm-art-cav">{{ ca.cavidades }} cav.</span>
      </div>
    </div>
    <div v-else class="cm-arts cm-arts--empty">Sin artículos</div>

    <!-- Tiempo programado de la OT: su duración teórica, sin la holgura del plan. -->
    <div class="cm-prog">
      <span class="cm-prog-label">Tiempo prog. OT:</span>
      <span class="cm-prog-value">{{ duracionTxt }}</span>
    </div>

    <!-- Piezas programadas de toda la OT. Solo en el detalle. -->
    <div v-if="detalle" class="cm-prog">
      <span class="cm-prog-label">Piezas programadas</span>
      <span class="cm-prog-value">{{ piezasProgTxt }}</span>
    </div>

    <!-- Molde con el que la máquina está produciendo ahora. -->
    <div v-if="item.moldeEnCurso" class="cm-encurso">
      <span class="cm-encurso-dot"></span>
      Activa con <b class="cm-encurso-dato">{{ item.moldeEnCurso }}</b>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'CardCambioMolde',

  props: {
    item: { type: Object, default: () => ({}) },
    // Modo detalle (dentro del diálogo): agrega el tiempo de retraso y deja de ser clickeable.
    detalle: { type: Boolean, default: false },
    // "Ahora" según el backend (FechaConsulta). Referencia del retraso, igual para todas las
    // tarjetas para que no dependan del reloj del navegador.
    ahora: { type: Number, default: 0 },
  },

  computed: {
    cavidades() {
      return Array.isArray(this.item.cavidadesArticulos) ? this.item.cavidadesArticulos : [];
    },

    // Minutos hasta el arranque. Negativo = el cambio ya debió haber ocurrido.
    diffMin() {
      const inicio = moment(this.item.inicioSiguiente);
      if (!this.item.inicioSiguiente || !inicio.isValid() || !this.ahora) return null;
      return Math.round((inicio.valueOf() - this.ahora) / 60000);
    },

    retrasado() {
      return this.diffMin != null && this.diffMin < 0;
    },

    colorLateral() {
      return this.retrasado ? '#E83535' : '#2B7EFF';
    },

    faltanteTxt() {
      if (this.diffMin == null) return '';

      const abs = Math.abs(this.diffMin);
      if (abs < 1) return 'ahora';

      const horas = Math.floor(abs / 60);
      const min = abs % 60;
      const dur = horas ? `${horas} h ${min} min` : `${min} min`;
      return this.diffMin > 0 ? `en ${dur}` : `retrasado ${dur}`;
    },

    piezasProgTxt() {
      return this.numTxt(this.item.piezasProgramadas);
    },

    // Duración teórica de la OT, sin la holgura del plan.
    duracionTxt() {
      const seg = Number(this.item.duracionSegundos);
      if (!Number.isFinite(seg) || seg <= 0) return '—';
      const totalMin = Math.round(seg / 60);
      const horas = Math.floor(totalMin / 60);
      const min = totalMin % 60;
      return horas ? `${horas} h ${min} min` : `${min} min`;
    },

    // Las fechas del tablero llegan en ISO local sin zona ("yyyy-MM-ddTHH:mm:ss").
    horaTxt() {
      const m = moment(this.item.inicioSiguiente);
      if (!this.item.inicioSiguiente || !m.isValid()) return '—';
      return m.format('DD/MM HH:mm');
    },
  },

  methods: {
    numTxt(valor) {
      const n = Number(valor);
      return Number.isFinite(n) ? Math.round(n).toLocaleString('es-MX') : '0';
    },

    // Lado del artículo según su código, misma lógica que las tarjetas del estado de máquinas:
    // la letra que sigue al bloque "letras + números" (VAR10L003 -> LH), no cualquier L/R del
    // código, para que la R del prefijo no marque ambos lados.
    sufijoLado(codigo) {
      if (!codigo) return '';
      const m = String(codigo).match(/^[A-Za-z]+\d+([LR])/i);
      if (!m) return '';
      return m[1].toUpperCase() === 'R' ? ' RH' : ' LH';
    },
  },
};
</script>

<style scoped>
/* Las tarjetas de una fila se estiran a la altura de la más alta (la que tiene más artículos).
   Con flex + justify-content:center el contenido queda centrado verticalmente en vez de
   apelotonado arriba dejando un hueco abajo. */
.cm-card {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 14px;
  padding: 9px 14px 9px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  font-family: 'Space Grotesk', 'Roboto', sans-serif;
  line-height: 1.15;
  overflow: hidden;
}

/* La franja se posiciona contra la caja de padding, así que queda por dentro del borde: con OT
   crítica el marco rojo la rodea sin taparla. */
.cm-card-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  border-radius: 14px 0 0 14px;
}

.cm-card--click {
  cursor: pointer;
  transition: box-shadow 0.18s, transform 0.18s, border-color 0.18s;
}

.cm-card--click:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  border-color: rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.cm-card--click:active {
  transform: translateY(0);
}

/* Molde crítico (TrabajoOrden.OTCritica): marco rojo, igual que el plan teórico en V4. */
.cm-card--critica {
  border: 3px solid #E83535;
}

.cm-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.cm-block {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.1;
}

.cm-block--der {
  flex: 0 0 auto;
  align-items: flex-end;
  text-align: right;
}

.cm-block-label {
  margin-bottom: 1px;
  font-size: 9.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8A95A3;
  white-space: nowrap;
}

.cm-molde-code {
  font-size: 18px;
  font-weight: 700;
  color: #0D1117;
  letter-spacing: -0.03em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cm-maq-code {
  font-size: 18px;
  font-weight: 700;
  color: #2B7EFF;
  letter-spacing: -0.03em;
  white-space: nowrap;
}

/* Datos de tiempo */
.cm-tiempo {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 7px;
  margin-top: 6px;
}

.cm-hora {
  font-size: 14px;
  font-weight: 700;
  color: #0D1117;
  white-space: nowrap;
}

.cm-falta {
  font-size: 11px;
  font-weight: 500;
  color: #5A6476;
  white-space: nowrap;
}

.cm-falta--retraso {
  font-weight: 700;
  color: #E83535;
}

/* Máquina ocupada: ámbar, el mismo tono con el que el estado de máquinas marca el paro
   programado. Va a la derecha de la fila de tiempo. */
.cm-ocupada {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid rgba(255, 159, 0, 0.25);
  background: rgba(255, 159, 0, 0.1);
  font-size: 10px;
  font-weight: 700;
  color: #B26B00;
  white-space: nowrap;
}

.cm-ocupada-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #FF9F00;
}

.cm-ot {
  margin-top: 6px;
  font-size: 11px;
  color: #5A6476;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cm-ot-dato {
  font-weight: 700;
  color: #0D1117;
}

/* Aviso de molde en curso distinto. Azul, el mismo color de "activa" del estado de máquinas. */
.cm-encurso {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  font-size: 10.5px;
  color: #5A6476;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cm-encurso-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #2B7EFF;
}

.cm-encurso-dato {
  font-weight: 700;
  color: #0D1117;
}

/* Artículos y cavidades: mismo tratamiento que las tarjetas del estado de máquinas. */
.cm-arts {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}

.cm-arts--empty {
  font-size: 11px;
  color: #B4BCC7;
  font-style: italic;
}

.cm-art-row {
  display: flex;
  align-items: center;
  gap: 7px;
}

/* En el detalle la meta va centrada entre el código y las cavidades, igual que en el estado de
   máquinas: grid de 5 columnas (punto, código, meta, hueco, cavidades). */
.cm-art-row--detalle {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr auto;
  align-items: center;
}

.cm-art-meta {
  grid-column: 3;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #5A6476;
  white-space: nowrap;
}

.cm-art-row--detalle .cm-art-cav {
  grid-column: 5;
  justify-self: end;
}

.cm-art-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #B4BCC7;
}

.cm-art-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 11px;
  font-weight: 700;
  color: #8A95A3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cm-art-cav {
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 8px;
  background: #F3F5F9;
  font-size: 9.5px;
  font-weight: 700;
  color: #8A95A3;
}

/* Cierre de la tarjeta, separado con una línea. En el detalle hay dos renglones (tiempo y
   piezas); el segundo no repite el divisor. */
.cm-prog {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  padding-top: 5px;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
}

.cm-prog + .cm-prog {
  margin-top: 2px;
  padding-top: 0;
  border-top: none;
}

.cm-prog-label {
  font-size: 9.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8A95A3;
  /* La etiqueta cede espacio y se acomoda en 2 líneas si hace falta, para que el valor
     (a la derecha) nunca se recorte. */
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.15;
  white-space: normal;
}

.cm-prog-value {
  font-size: 15px;
  font-weight: 700;
  color: #0D1117;
  white-space: nowrap;
  /* Prioridad al valor: nunca se encoge ni se corta (ej. "12 h 16 min"). */
  flex: 0 0 auto;
  text-align: right;
}
</style>
